import React, { useState, useEffect } from 'react';
import { Route, Switch, Redirect } from "react-router-dom";
import { Routes } from "../routes";

// pages
import DashboardOverview from "./dashboard/DashboardOverview";
import Transactions from "./Transactions";
import Settings from "./Settings";
import Welcome from "./Welcome";
import NotFoundPage from "./examples/NotFound";
import Company from "./company/Company";
import Employee from "./employee/Employee";
import EditOrganisation from "./editorganization/EditOrganisation";
import AddEntities from "./addentities/AddEntities";
import AddDepartment from "./addentities/AddDepartment";
import EditDepartment from "./addentities/EditDepartment";
import AddWorkPlaces from "./addworkplaces/AddWorkPlaces";
import EditWorkPlaces from "./addworkplaces/EditWorkPlaces";
import PermissionGroup from "./permissions/PermissionGroup";
import AddPermissionGroup from "./permissions/AddPermissionGroup";
import Brand from "./brand/brand";
import AddBrand from "./brand/AddBrand";
import EditBrand from "./brand/EditBrand";
import Category from "./category/category";
import AddCategory from "./category/AddCategory";
import EditCategory from "./category/EditCategory";
import Spusku from "./spusku/spusku";
import AddSpu from "./spusku/AddSpu";
import EditSpu from "./spusku/EditSpu";
import SkuPresets from "./skupresets/SkuPresets";
import BulkImport from './skupresets/BulkImport';
import Branch from './branch/branch';
import AddBranch from './branch/AddBranch';
import EditBranch from './branch/EditBranch';
import StockList from './stocklist/StockList';
import ListingPricing from './listingpricing/ListingPricing';
import AddListing from './listingpricing/AddListing';
import EditListing from './listingpricing/EditListing';
import BulkPricing from './listingpricing/BulkPricing';
import PlatformOrder from './platformorder/PlatformOrder';
import LinkStockInstance from './platformorder/LinkStockInstance';
import PlatformOrderLinked from './platformorder/PlatformOrderLinked';
import AbbreviationsDictionary from './skuscheme/AbbreviationsDictionary';
import SkuScheme from './skuscheme/SkuScheme';

// docs
import DocsOverview from "./documentation/DocsOverview";
import DocsDownload from "./documentation/DocsDownload";

// components
import Sidebar from "../components/Sidebar";
import Signin from "../components/Signin";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Preloader from "../components/Preloader";
import Department from './department/Department';
import AddEmployee from "./employee/AddEmployee";
import EditEmployee from "./employee/EditEmployee";
import EditEntities from "./addentities/EditEntities";

const RouteWithLoader = ({ component: Component, ...rest }) => {
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setLoaded(true), 1000);
    return () => clearTimeout(timer);
  }, []);
  return (
    <Route
      {...rest}
      render={props => (
        <>
          <Preloader show={!loaded} />
          {loaded && <Component {...props} />}
        </>
      )}
    />
  );
};

const RouteWithNavbar = ({ component: Component, ...rest }) => {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setLoaded(true), 1000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <Route
      {...rest}
      render={props => (
        <>
          <Preloader show={!loaded} />
          {loaded && (
            <>
              <main className="content">
                <Navbar />
                <Component {...props} />
                <Footer />
              </main>
            </>
          )}
        </>
      )}
    />
  );
};

// ✅ FIXED VERSION HERE
const RouteWithSidebar = ({ component: Component, ...rest }) => {
  const [loaded, setLoaded] = useState(false);
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  const [collapsed, setCollapsed] = useState(window.innerWidth <= 1023);

  useEffect(() => {
    const timer = setTimeout(() => setLoaded(true), 1000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      const isMobile = window.innerWidth <= 767;
      const isTablet = window.innerWidth > 767 && window.innerWidth <= 1023;
      setCollapsed(isTablet || isMobile);
      if (!isMobile) setShowMobileSidebar(false);
    };

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const toggleMobileSidebar = () => setShowMobileSidebar(prev => !prev);
  const toggleCollapse = () => setCollapsed(prev => !prev);

  const localStorageIsSettingsVisible = () => {
    return localStorage.getItem('settingsVisible') === 'false' ? false : true;
  };

  const [showSettings, setShowSettings] = useState(localStorageIsSettingsVisible());
  const toggleSettings = () => {
    const newValue = !showSettings;
    setShowSettings(newValue);
    localStorage.setItem('settingsVisible', newValue);
  };

  // ✅ Authentication check
  const token = localStorage.getItem('token');
  if (!token) {
    return <Redirect to="/" />;
  }

  return (
    <Route
      {...rest}
      render={props => (
        <>
          <Preloader show={!loaded} />
          {loaded && (
            <>
              <Sidebar
                showMobile={showMobileSidebar}
                toggleMobile={toggleMobileSidebar}
                collapsed={collapsed}
                toggleCollapse={toggleCollapse}
              />

              <main className="content">
                <Navbar onMobileToggleClick={toggleMobileSidebar} />
                <Component {...props} />
                <Footer toggleSettings={toggleSettings} showSettings={showSettings} />
              </main>
            </>
          )}
        </>
      )}
    />
  );
};

export default () => (
  <Switch>
    {/* dashboard and other pages with sidebar and navbar */}
    <RouteWithSidebar exact path={Routes.DashboardOverview.path} component={DashboardOverview} />
    <RouteWithSidebar exact path={Routes.Transactions.path} component={Transactions} />
    <RouteWithSidebar exact path={Routes.Settings.path} component={Settings} />
    <RouteWithSidebar exact path={Routes.Company.path} component={Company} />
    <RouteWithSidebar exact path={Routes.Employee.path} component={Employee} />
    <RouteWithSidebar exact path={Routes.Department.path} component={Department} />
    <RouteWithSidebar exact path={Routes.EditOrganisation.path} component={EditOrganisation} />
    <RouteWithSidebar exact path={Routes.AddEntities.path} component={AddEntities} />
    <RouteWithSidebar exact path={Routes.AddDepartment.path} component={AddDepartment} />
    <RouteWithSidebar exact path={Routes.EditDepartment.path} component={EditDepartment} />
    <RouteWithSidebar exact path={Routes.AddEmployee.path} component={AddEmployee} />
    <RouteWithSidebar exact path={Routes.AddWorkPlaces.path} component={AddWorkPlaces} />
    <RouteWithSidebar exact path={Routes.EditWorkPlaces.path} component={EditWorkPlaces} />
    <RouteWithSidebar exact path={Routes.PermissionGroup.path} component={PermissionGroup} />
    <RouteWithSidebar exact path={Routes.AddPermissionGroup.path} component={AddPermissionGroup} />
    <RouteWithSidebar exact path={Routes.EditEmployee.path} component={EditEmployee} />
    <RouteWithSidebar exact path={Routes.EditEntities.path} component={EditEntities} />
    <RouteWithSidebar exact path={Routes.Brand.path} component={Brand} />
    <RouteWithSidebar exact path={Routes.AddBrand.path} component={AddBrand} />
    <RouteWithSidebar exact path={Routes.EditBrand.path} component={EditBrand} />
    <RouteWithSidebar exact path={Routes.Category.path} component={Category} />
    <RouteWithSidebar exact path={Routes.AddCategory.path} component={AddCategory} />
    <RouteWithSidebar exact path={Routes.EditCategory.path} component={EditCategory} />
    <RouteWithSidebar exact path={Routes.Spusku.path} component={Spusku} />
    <RouteWithSidebar exact path={Routes.AddSpu.path} component={AddSpu} />
    <RouteWithSidebar exact path={Routes.EditSpu.path} component={EditSpu} />
    <RouteWithSidebar exact path={Routes.SkuPresets.path} component={SkuPresets} />
    <RouteWithSidebar exact path={Routes.BulkImport.path} component={BulkImport} />
    <RouteWithSidebar exact path={Routes.Branch.path} component={Branch} />
    <RouteWithSidebar exact path={Routes.AddBranch.path} component={AddBranch} />
    <RouteWithSidebar exact path={Routes.EditBranch.path} component={EditBranch} />
    <RouteWithSidebar exact path={Routes.StockList.path} component={StockList} />
    <RouteWithSidebar exact path={Routes.ListingPricing.path} component={ListingPricing} />
    <RouteWithSidebar exact path={Routes.AddListing.path} component={AddListing} />
    <RouteWithSidebar exact path={Routes.EditListing.path} component={EditListing} />
    <RouteWithSidebar exact path={Routes.BulkPricing.path} component={BulkPricing} />
    <RouteWithSidebar exact path={Routes.PlatformOrder.path} component={PlatformOrder} />
    <RouteWithSidebar exact path={Routes.LinkStockInstance.path} component={LinkStockInstance} />
    <RouteWithSidebar exact path={Routes.PlatformOrderLinked.path} component={PlatformOrderLinked} />
    <RouteWithSidebar exact path={Routes.AbbreviationsDictionary.path} component={AbbreviationsDictionary} />
    <RouteWithSidebar exact path={Routes.SkuScheme.path} component={SkuScheme} />

    {/* signin page WITHOUT sidebar and navbar */}
    <Route exact path={Routes.Signin.path} component={Signin} />

    {/* Welcome page WITH navbar only */}
    <RouteWithNavbar exact path={Routes.Welcome.path} component={Welcome} />

    {/* docs pages with sidebar and navbar */}
    <RouteWithSidebar exact path={Routes.DocsOverview.path} component={DocsOverview} />
    <RouteWithSidebar exact path={Routes.DocsDownload.path} component={DocsDownload} />

    {/* fallback */}
    <RouteWithSidebar component={NotFoundPage} />
  </Switch>
);
